const PROJECT_TEMPLATES = {
    dating: {
        title: 'Dating Profile',
        goal: 'Choose the best dating photo, improve the rest, and lift match quality over time.',
        summary: 'Build a stronger dating lineup with a better lead photo, clearer prompts, and outfit feedback.',
        modules: [
            {
                id: 'photos',
                label: 'Photos',
                description: 'Choose a better first photo and build a more complete lineup.',
            },
            {
                id: 'profile',
                label: 'Bio & prompts',
                description: 'Tighten the written parts of your dating profile to match your photos.',
            },
            {
                id: 'outfit',
                label: 'Outfit',
                description: 'Review vibe, fit, and styling choices that change first impressions.',
            },
            {
                id: 'results',
                label: 'Results',
                description: 'Track whether better photos lead to more matches and better conversations.',
            },
        ],
        nextActions: [
            {
                id: 'upload-lineup',
                title: 'Upload 4-6 dating photos',
                detail: 'Start with a strong mix of solo, full-body, and lifestyle shots.',
                category: 'photos',
                priority: 'high',
            },
            {
                id: 'pick-lead',
                title: 'Choose your best first photo',
                detail: 'Use the strongest shot as your opener and save supporting photos for later slots.',
                category: 'photos',
                priority: 'high',
            },
            {
                id: 'tighten-profile',
                title: 'Tighten prompts and bio',
                detail: 'Make sure your written profile reinforces the same vibe as your photos.',
                category: 'profile',
                priority: 'medium',
            },
            {
                id: 'outfit-check',
                title: 'Review outfit and vibe',
                detail: 'Check whether styling helps you look current, warm, and intentional.',
                category: 'outfit',
                priority: 'medium',
            },
            {
                id: 'log-results',
                title: 'Track match results',
                detail: 'Log whether profile changes actually improved your matches or replies.',
                category: 'results',
                priority: 'medium',
            },
        ],
        profileSections: [
            {
                id: 'bio',
                label: 'Bio',
                placeholder: 'Builder by day. Pasta loyalist by night. Better in person than on text.',
                helper: 'Keep it short, specific, and easy to reply to.',
            },
            {
                id: 'prompt-1',
                label: 'Prompt 1',
                placeholder: 'A prompt answer that shows humor or warmth.',
                helper: 'Lead with something that feels natural in conversation.',
            },
            {
                id: 'prompt-2',
                label: 'Prompt 2',
                placeholder: 'A prompt answer that shows your lifestyle or interests.',
                helper: 'Use a concrete detail instead of a vague list.',
            },
            {
                id: 'prompt-3',
                label: 'Prompt 3',
                placeholder: 'A prompt answer that gives someone an easy opener.',
                helper: 'Make it easy for the right person to start the conversation.',
            },
        ],
    },
    linkedin: {
        title: 'LinkedIn Profile',
        goal: 'Choose the best headshot, tighten the professional profile around it, and track career outcomes.',
        summary: 'Build a stronger professional first impression with a better headshot, sharper copy, and cleaner styling.',
        modules: [
            {
                id: 'photos',
                label: 'Headshot',
                description: 'Choose the most credible and approachable LinkedIn photo.',
            },
            {
                id: 'profile',
                label: 'Headline & About',
                description: 'Sharpen your written profile so it matches the image you want to project.',
            },
            {
                id: 'outfit',
                label: 'Outfit',
                description: 'Review wardrobe, polish, and background fit for your professional goal.',
            },
            {
                id: 'results',
                label: 'Career results',
                description: 'Track profile views, recruiter messages, and response quality over time.',
            },
        ],
        nextActions: [
            {
                id: 'upload-lineup',
                title: 'Compare 2-4 headshots',
                detail: 'Start with a few clear options so the app can pick the strongest professional signal.',
                category: 'photos',
                priority: 'high',
            },
            {
                id: 'pick-lead',
                title: 'Choose your best LinkedIn headshot',
                detail: 'Pick the photo that balances competence, trust, and approachability.',
                category: 'photos',
                priority: 'high',
            },
            {
                id: 'tighten-profile',
                title: 'Refine headline and About',
                detail: 'Make your copy support the same role, seniority, and tone as the chosen headshot.',
                category: 'profile',
                priority: 'medium',
            },
            {
                id: 'outfit-check',
                title: 'Review outfit and polish',
                detail: 'Use styling that feels current and aligned with your role or industry.',
                category: 'outfit',
                priority: 'medium',
            },
            {
                id: 'log-results',
                title: 'Track views and recruiter replies',
                detail: 'Measure whether the new profile improves profile views or outbound response.',
                category: 'results',
                priority: 'medium',
            },
        ],
        profileSections: [
            {
                id: 'headline',
                label: 'Headline',
                placeholder: 'Product leader scaling AI tools for trust and growth',
                helper: 'Lead with your current role, market, and value in one line.',
            },
            {
                id: 'about',
                label: 'About',
                placeholder: 'Write a short narrative about what you do, who you help, and the work you are proud of.',
                helper: 'Make it readable in short paragraphs and tie it to the role you want next.',
            },
            {
                id: 'banner',
                label: 'Banner',
                placeholder: 'Describe the banner direction you want, such as product shots, speaking photo, or a clean brand statement.',
                helper: 'Your banner should reinforce your role, not distract from it.',
            },
        ],
    },
};
function createProfileWorkspace(vertical) {
    return {
        sections: PROJECT_TEMPLATES[vertical].profileSections.map(section => ({
            ...section,
            value: '',
            draftVersions: [],
            analysis: null,
        })),
        suggestions: [],
        polishChecklist: [],
        audit: null,
        updatedAt: null,
    };
}
function createOutfitWorkspace() {
    return {
        notes: '',
        review: null,
        updatedAt: null,
    };
}
export function getProjectIdForVertical(vertical) {
    return `${vertical}-workspace`;
}
export function createDefaultProject(vertical, updatedAt = new Date().toISOString()) {
    const template = PROJECT_TEMPLATES[vertical];
    return {
        id: getProjectIdForVertical(vertical),
        vertical,
        title: template.title,
        goal: template.goal,
        status: 'setup',
        summary: template.summary,
        currentScore: null,
        bestScore: null,
        latestPhotoId: null,
        bestPhotoId: null,
        photoIds: [],
        modules: template.modules.map(module => ({
            ...module,
            status: 'not_started',
            completion: 0,
        })),
        nextActions: template.nextActions.map(action => ({
            ...action,
            completed: false,
        })),
        profileWorkspace: createProfileWorkspace(vertical),
        outfitWorkspace: createOutfitWorkspace(),
        compareRuns: [],
        updatedAt,
    };
}
export function createDefaultProjects(updatedAt = new Date().toISOString()) {
    return ['dating', 'linkedin'].map(vertical => createDefaultProject(vertical, updatedAt));
}
/**
 * Merge persisted project data with the current defaults so older saved rows
 * automatically pick up any new module labels, fields, or actions added later.
 */
export function mergeProjectsWithDefaults(projects, updatedAt = new Date().toISOString()) {
    const persisted = projects || [];
    return createDefaultProjects(updatedAt).map(defaultProject => {
        const existing = persisted.find(project => project.id === defaultProject.id) ||
            persisted.find(project => project.vertical === defaultProject.vertical);
        if (!existing)
            return defaultProject;
        const modulesById = new Map((existing.modules || []).map(module => [module.id, module]));
        const actionsById = new Map((existing.nextActions || []).map(action => [action.id, action]));
        const profileSectionById = new Map((existing.profileWorkspace?.sections || []).map(section => [section.id, section]));
        return {
            ...defaultProject,
            ...existing,
            id: defaultProject.id,
            vertical: defaultProject.vertical,
            title: existing.title || defaultProject.title,
            goal: existing.goal || defaultProject.goal,
            summary: existing.summary || defaultProject.summary,
            photoIds: Array.from(new Set(existing.photoIds || [])),
            modules: defaultProject.modules.map(module => {
                const saved = modulesById.get(module.id);
                return saved
                    ? {
                        ...module,
                        status: saved.status,
                        completion: saved.completion,
                    }
                    : module;
            }),
            nextActions: defaultProject.nextActions.map(action => {
                const saved = actionsById.get(action.id);
                return saved
                    ? {
                        ...action,
                        completed: saved.completed,
                    }
                    : action;
            }),
            profileWorkspace: {
                sections: defaultProject.profileWorkspace.sections.map(section => ({
                    ...section,
                    ...(profileSectionById.get(section.id) || {}),
                    id: section.id,
                    label: section.label,
                    placeholder: section.placeholder,
                    helper: section.helper,
                    draftVersions: profileSectionById.get(section.id)?.draftVersions || [],
                    analysis: profileSectionById.get(section.id)?.analysis || null,
                })),
                suggestions: existing.profileWorkspace?.suggestions || [],
                polishChecklist: existing.profileWorkspace?.polishChecklist || [],
                audit: existing.profileWorkspace?.audit || null,
                updatedAt: existing.profileWorkspace?.updatedAt || defaultProject.profileWorkspace.updatedAt,
            },
            outfitWorkspace: {
                notes: existing.outfitWorkspace?.notes || '',
                review: existing.outfitWorkspace?.review || null,
                updatedAt: existing.outfitWorkspace?.updatedAt || defaultProject.outfitWorkspace.updatedAt,
            },
            compareRuns: (existing.compareRuns || []).map(run => ({
                ...run,
                rankings: [...run.rankings].sort((a, b) => b.score - a.score),
            })),
            updatedAt: existing.updatedAt || defaultProject.updatedAt,
        };
    });
}
